import { useTranslation } from "react-i18next";
import _ from "lodash";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import { EGAccordionSingle } from "../../../../MainComponents/Accordion/EGAccordion/EGAccordion";
import Icon from "../../../../Icon/Icon";
import { InvestigationItemContainer } from "../components/InvestigationItemContainer";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { useMemo, useEffect, useState } from "react";
import { InvestigationBlockInfo } from "../components/InvestigationBlockInfo";
import { catalogByPart } from "../../../../../apis";
import {
  InvestigationCollapsable,
  Separator,
  UploadedFile,
} from "../components/InvestigationComponents";
import { transformDateAndHourToString } from "../helpers/formatDate";
import { parseConclusion } from "../helpers/conclusionParser";

export const SingleConclusion = ({
  conclusion,
  setIsEditing,
  handleSaveConclusionDetails,
  justView,
  assignsDetails = [],
}) => {
  const [t] = useTranslation("investigation");
  const theme = useTheme();

  const [mainEvidenceOpts, setMainEvidenceOpts] = useState([]);

  // Parsear la conclusión para separar general de conclusiones por reporte
  const { generalConclusion, reportConclusions } = useMemo(() => {
    return parseConclusion(conclusion.conclusion);
  }, [conclusion.conclusion]);

  const hasMultipleReports = assignsDetails.length > 1;

  // Cargar catálogo de tipos de evidencia principal
  useEffect(() => {
    const loadMainEvidenceCatalog = async () => {
      try {
        const resp = await catalogByPart({
          is_own: true,
          catalogue: "mainEvidenceInTheConclusion",
          path: "/",
        });

        if (resp?.data && Array.isArray(resp.data)) {
          const formattedOptions = resp.data.map((item) => ({
            value: item.key,
            label: item.label,
          }));
          setMainEvidenceOpts(formattedOptions);
        }
      } catch (error) {
        console.error("Error loading main evidence catalog:", error);
        setMainEvidenceOpts([]);
      }
    };

    loadMainEvidenceCatalog();
  }, []);

  const conclusionDate = useMemo(() => {
    const [fecha, hora] = transformDateAndHourToString(
      conclusion.conclusion_at
    );
    return `${fecha ? fecha.toLocaleUpperCase() : t("Conclusion.not_scheduled")} ${
      hora ? " - " + hora.toLocaleLowerCase() : ""
    }`;
  }, [conclusion.conclusion_at]);

  const mainEvidenceLabel = useMemo(() => {
    if (!conclusion.main_evidence) return null;
    const found = mainEvidenceOpts.find((opt) => opt.value === conclusion.main_evidence);
    return found?.label || conclusion.main_evidence;
  }, [conclusion.main_evidence, mainEvidenceOpts]);

  const handleEditConclusion = () => {
    // setSelectedConclusion(idx);
    setIsEditing(true);
  };

  const handleRemoveConclusion = () => {
    handleSaveConclusionDetails({
      conclusion: conclusion.conclusion,
      comment: conclusion.comment,
      conclusion_at: null,
      folder: {
        main_evidence: conclusion.main_evidence,
        conclusion: {
          files: [],
          measures: [],
        },
      },
    });
  };

  const actions = [
    {
      name: t("speeddial.edit"),
      icon: <Icon name="outline_edit" color={theme.palette.secondary.main} />,
      onClick: handleEditConclusion,
    },
    // ...( conclusion.conclusion_at ? [
    //   {
    //     name: t("speeddial.delete"),
    //     icon: <Icon name="trash_can" color={theme.palette.error.main} />,
    //     onClick: handleRemoveConclusion,
    //   },
    // ] : []),
  ];

  // console.log("Conclusion");
  // console.log(conclusion);

  return (
    <EGAccordionSingle
      title={t("Conclusion.single_conclusion_title")}
      bgColor={"#7BBDFF"}
      color={theme.palette.white.main}
      actions={!justView ? actions : undefined}
    >
      <InvestigationItemContainer sx={{ p: 2 }} color={"#F26D6D"}>
        <Typography color={theme.palette.blueGray.main}>
          {t("Conclusion.conclusion_date")}:&nbsp;
          <Typography component="span" fontWeight={700} color={"#F26D6D"}>
            {conclusionDate}
          </Typography>
        </Typography>

        <hr className="hr-div" />

        <Grid2 container spacing={2}>
          <InvestigationBlockInfo
            label={t("Conclusion.conclusion_description")}
            text={generalConclusion}
            richTextEditor={true}
            size={12}
          />
          {mainEvidenceLabel && (
            <InvestigationBlockInfo
              label="Tipo de prueba principal considerada"
              text={mainEvidenceLabel}
              size={12}
            />
          )}
          {conclusion.comment && (
            <InvestigationBlockInfo
              label={t(
                "Conclusion.details_of_investigation_and_observer"
              )}
              text={conclusion.comment}
              size={12}
            />
          )}
        </Grid2>
      </InvestigationItemContainer>

      {conclusion.files.length > 0 && (
        <>
          <Separator
            text={t("Conclusion.support_files_for_the_conclusion")}
            sx={{ my: 2 }}
          />
          <Grid2 container spacing={2}>
            {Array.isArray(conclusion.files) &&
              conclusion.files.map((file, idx) => (
                <Grid2 size={{ xs: 12, lg: 6 }} key={idx}>
                  <UploadedFile
                    text={file.name}
                    description={file.details}
                    downloadUrl={file.url}
                    width="100%"
                  />
                </Grid2>
              ))}
          </Grid2>
        </>
      )}

      {Array.isArray(conclusion.measures) && conclusion.measures.length > 0 && (
        <>
          <Separator
            text={t(
              "Conclusion.measures_corrective_description"
            )}
            sx={{ my: 2 }}
          />
          <Stack spacing={2}>
            {conclusion.measures.map((measure, idx) => (
              <InvestigationCollapsable
                key={idx}
                title={
                  <Typography
                    fontSize={14}
                    fontWeight={600}
                    color={theme.palette.blueGray600.main}
                  >
                    {measure.measure}
                  </Typography>
                }
                details={
                  <>
                    <Typography
                      fontSize={14}
                      fontWeight={600}
                      color={theme.palette.blueGray600.main}
                    >
                      {t("Conclusion.details")}:
                      <Typography
                        component="span"
                        fontSize={12}
                        fontWeight={400}
                        color={theme.palette.oxfordBlue.main}
                        fontStyle="italic"
                      >
                        {measure.details}
                      </Typography>
                    </Typography>
                  </>
                }
              />
            ))}
          </Stack>
        </>
      )}

      {/* Conclusiones por reporte - solo si hay más de 1 denuncia */}
      {hasMultipleReports && reportConclusions.length > 0 && (
        <>
          <Separator
            text={t("Conclusion.conclusions_by_report")}
            sx={{ my: 2 }}
          />
          <Stack spacing={2}>
            {reportConclusions.map((rc, idx) => (
              <Box
                key={idx}
                sx={{
                  border: `1px solid ${theme.palette.blueGray200.main}`,
                  borderRadius: "8px",
                  p: 2,
                  backgroundColor: theme.palette.blueGray50.main,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Typography fontWeight={600} color="primary">
                    {rc.folio}
                  </Typography>
                  <Typography color="secondary" fontSize={14}>
                    | {rc.classification_label}
                  </Typography>
                </Stack>
                <InvestigationBlockInfo
                  label={t("Conclusion.conclusion_description")}
                  text={rc.conclusion}
                  richTextEditor={true}
                  size={12}
                />
              </Box>
            ))}
          </Stack>
        </>
      )}
    </EGAccordionSingle>
  );
};
